import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { siteInfo, stats } from '@/mocks/site';

export default function HeroSection() {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const navigate = useNavigate();

  const results = useMemo(() => {
    const keyword = query.trim();
    if (!keyword) return [];
    return articles
      .filter(
        (item) =>
          item.title.includes(keyword) ||
          item.summary.includes(keyword) ||
          item.tags.some((tag) => tag.includes(keyword)),
      )
      .slice(0, 5);
  }, [query]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (results.length > 0) {
      navigate(`/article/${results[0].slug}`);
      return;
    }
    document.getElementById('events')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative w-full">
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://readdy.ai/api/search-image?query=Cinematic%20abstract%20background%20with%20layered%20translucent%20shapes%2C%20deep%20warm%20coral%20red%20and%20amber%20gradients%20fading%20into%20dark%20charcoal%2C%20soft%20film%20grain%2C%20gentle%20light%20bloom%2C%20moody%20editorial%20poster%20aesthetic%2C%20high%20contrast%20composition%20for%20text%20overlay&width=1800&height=1000&seq=gua-hero-01&orientation=landscape"
          alt="17c吃瓜今日热门事件专题背景图"
          title="17c吃瓜 · 今日热门事件实时更新"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-950/55 to-foreground-950/75"></div>
      </div>

      <div className="relative w-full px-4 md:px-8 pt-16 md:pt-24 pb-14 md:pb-20">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 text-xs md:text-sm text-background-50 bg-primary-500/90 px-3.5 py-1.5 rounded-full">
            <span className="w-2 h-2 rounded-full bg-background-50 animate-pulse"></span>
            {siteInfo.updateFrequency}
          </span>

          <h1 className="mt-5 md:mt-6 font-heading text-3xl md:text-5xl lg:text-6xl font-black text-background-50 leading-tight">
            17c吃瓜
            <span className="block mt-2 text-xl md:text-3xl lg:text-4xl font-bold text-secondary-300">
              今日热门事件实时更新
            </span>
          </h1>

          <p className="mt-5 md:mt-6 text-sm md:text-lg text-background-200 leading-relaxed max-w-2xl mx-auto">
            聚焦全网热议话题，深挖最新爆料内幕。把零散的截图与传闻重新排成时间线，三分钟看懂一件事的
            <strong className="font-semibold text-background-50">完整脉络、关键细节与网友焦点</strong>。
          </p>

          <form onSubmit={handleSubmit} className="mt-7 md:mt-9 max-w-xl mx-auto relative" role="search">
            <label htmlFor="hero-search" className="sr-only">
              搜索热门事件
            </label>
            <div className="flex items-center gap-2 bg-background-50 rounded-full pl-4 pr-1.5 py-1.5">
              <i className="ri-search-line text-foreground-500 text-base"></i>
              <input
                id="hero-search"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => window.setTimeout(() => setFocused(false), 150)}
                placeholder="搜一搜今天的热点，例如「退票」「解约」「剪辑」"
                className="flex-1 h-10 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
              />
              <button
                type="submit"
                className="h-10 px-5 text-sm rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                搜索
              </button>
            </div>

            {focused && query.trim() && (
              <div className="absolute left-0 right-0 top-full mt-2 rounded-lg border border-background-200 bg-background-50 overflow-hidden text-left z-20">
                {results.length > 0 ? (
                  results.map((item) => (
                    <Link
                      key={item.slug}
                      to={`/article/${item.slug}`}
                      className="block px-4 py-3 border-b border-background-100 last:border-b-0 hover:bg-primary-50 cursor-pointer"
                    >
                      <span className="block text-sm text-foreground-900">{item.title}</span>
                      <span className="mt-1 block text-xs text-foreground-500">
                        热度 {item.heat} · {item.publishedAt}
                      </span>
                    </Link>
                  ))
                ) : (
                  <p className="px-4 py-3 text-sm text-foreground-500">
                    暂时没有匹配的事件，换个关键词试试。
                  </p>
                )}
              </div>
            )}
          </form>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="px-3.5 py-1.5 text-xs md:text-sm rounded-full border border-background-50/40 text-background-100 hover:bg-background-50 hover:text-foreground-950 transition-colors cursor-pointer"
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-12 md:mt-16 max-w-5xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {stats.map((item) => (
            <div
              key={item.label}
              className="rounded-lg border border-background-50/20 bg-foreground-950/40 backdrop-blur-sm px-4 py-4 text-center"
            >
              <p className="font-heading text-xl md:text-3xl font-black text-secondary-300">{item.value}</p>
              <p className="mt-1 text-xs md:text-sm text-background-200">{item.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}